import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import AuthHeader from '../components/AuthHeader'
import { resourceFormalApi, type RFDeps, type RFDepNode } from '../utils/api'

// ============================================================================
// Layout: simple force-directed simulation, in-component, no extra deps.
// Each layer gets a horizontal band so the overall picture reads top-to-bottom.
// ============================================================================

const LAYER_ORDER = [
  'L1', 'L2', 'L3', 'L4',
  'SysLayer', 'Verifier',
  'Semantics', 'Corpus', 'Findings',
  'Umbrella', 'Qualtran', 'Other',
] as const

const LAYER_COLOR: Record<string, string> = {
  L1: '#f43f5e',         // rose
  L2: '#f59e0b',         // amber
  L3: '#10b981',         // emerald
  L4: '#0ea5e9',         // sky
  SysLayer: '#6366f1',   // indigo
  Verifier: '#a855f7',   // purple
  Semantics: '#64748b',  // slate
  Corpus: '#ec4899',     // pink
  Findings: '#06b6d4',   // cyan
  Umbrella: '#475569',   // slate dark
  Qualtran: '#84cc16',   // lime
  Other: '#94a3b8',
}

const LAYER_LABEL: Record<string, string> = {
  L1: 'L1 · Algorithm',
  L2: 'L2 · Logical gadgets',
  L3: 'L3 · PPM + Magic',
  L4: 'L4 · QEC code',
  SysLayer: 'SysLayer · Runtime invariants',
  Verifier: 'Verifier pipeline',
  Semantics: 'Semantic infrastructure',
  Corpus: 'Corpus papers',
  Findings: 'Cross-paper findings',
  Umbrella: 'Umbrella imports',
  Qualtran: 'Qualtran bridge',
  Other: 'Other',
}

type Positioned = RFDepNode & { x: number; y: number; vx: number; vy: number; r: number }

function layoutDeps(deps: RFDeps, width: number, height: number, activeLayers: Set<string>): Positioned[] {
  const visible = deps.nodes.filter(n => activeLayers.has(n.layer))
  if (visible.length === 0) return []

  // Group by layer; each layer gets a band on the canvas.
  const usedLayers = LAYER_ORDER.filter(L => visible.some(n => n.layer === L))
  const bandH = (height - 80) / Math.max(1, usedLayers.length)
  const bandY: Record<string, number> = {}
  usedLayers.forEach((L, i) => { bandY[L] = 40 + bandH * (i + 0.5) })

  // Initialise: jitter within the band.
  const nodes: Positioned[] = visible.map(n => {
    const layerNodes = visible.filter(v => v.layer === n.layer)
    const idx = layerNodes.indexOf(n)
    const spread = Math.max(1, layerNodes.length - 1)
    const x = 80 + ((width - 160) * idx) / spread
    return {
      ...n,
      x,
      y: bandY[n.layer] + (Math.random() - 0.5) * 24,
      vx: 0,
      vy: 0,
      r: 5,
    }
  })
  const byPath = new Map(nodes.map(n => [n.path, n]))

  // Build edges within the visible subset.
  const edges: [Positioned, Positioned][] = []
  for (const e of deps.edges) {
    const a = byPath.get(e.from), b = byPath.get(e.to)
    if (a && b) edges.push([a, b])
  }

  // Tiny stable force-directed sim — 150 iterations.
  const ITERS = 150
  const REPEL = 800
  const SPRING = 0.008
  const SPRING_LEN = 80
  const DAMPING = 0.85

  for (let iter = 0; iter < ITERS; iter++) {
    // Repulsion (n^2 — fine for ≤150 nodes).
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i]
      a.vx *= DAMPING; a.vy *= DAMPING
      for (let j = 0; j < nodes.length; j++) {
        if (i === j) continue
        const b = nodes[j]
        const dx = a.x - b.x, dy = a.y - b.y
        const dsq = dx * dx + dy * dy + 0.1
        const f = REPEL / dsq
        a.vx += (dx / Math.sqrt(dsq)) * f
        a.vy += (dy / Math.sqrt(dsq)) * f * 0.3   // dampened vertical repulsion to keep bands
      }
    }
    // Spring: keep edges short.
    for (const [a, b] of edges) {
      const dx = b.x - a.x, dy = b.y - a.y
      const d = Math.sqrt(dx * dx + dy * dy) + 0.1
      const f = (d - SPRING_LEN) * SPRING
      a.vx += (dx / d) * f
      a.vy += (dy / d) * f * 0.2
      b.vx -= (dx / d) * f
      b.vy -= (dy / d) * f * 0.2
    }
    // Band restoring force (keep each node near its layer's y).
    for (const a of nodes) {
      const target = bandY[a.layer]
      a.vy += (target - a.y) * 0.08
    }
    // Integrate.
    for (const a of nodes) {
      a.x += a.vx
      a.y += a.vy
      a.x = Math.max(40, Math.min(width - 40, a.x))
      a.y = Math.max(20, Math.min(height - 20, a.y))
    }
  }
  return nodes
}

// ============================================================================
// SVG renderer
// ============================================================================

function GraphSvg({
  deps, width, height, activeLayers, onSelect, selected, query,
}: {
  deps: RFDeps
  width: number
  height: number
  activeLayers: Set<string>
  onSelect: (path: string | null) => void
  selected: string | null
  query: string
}) {
  const positioned = useMemo(
    () => layoutDeps(deps, width, height, activeLayers),
    [deps, width, height, activeLayers],
  )
  const byPath = useMemo(() => new Map(positioned.map(n => [n.path, n])), [positioned])
  const matches = (n: RFDepNode) =>
    !query || n.path.toLowerCase().includes(query.toLowerCase())
      || n.module.toLowerCase().includes(query.toLowerCase())

  const focusSet = useMemo(() => {
    if (!selected) return new Set<string>()
    const s = new Set<string>([selected])
    const node = byPath.get(selected)
    if (node) {
      node.imports.forEach(p => s.add(p))
      node.imported_by.forEach(p => s.add(p))
    }
    return s
  }, [selected, byPath])

  return (
    <svg width={width} height={height} className="block">
      {/* Layer bands */}
      {LAYER_ORDER.filter(L => activeLayers.has(L) && positioned.some(n => n.layer === L)).map((L, i, arr) => {
        const bandH = (height - 80) / arr.length
        const y = 40 + bandH * i
        return (
          <g key={L}>
            <rect x={0} y={y} width={width} height={bandH} fill={LAYER_COLOR[L]} opacity={0.04} />
            <text x={12} y={y + 16} fontSize={11} fontWeight={700} fill={LAYER_COLOR[L]} opacity={0.7}>
              {LAYER_LABEL[L]}
            </text>
          </g>
        )
      })}

      {/* Edges */}
      {deps.edges.map((e, i) => {
        const a = byPath.get(e.from), b = byPath.get(e.to)
        if (!a || !b) return null
        const inFocus = focusSet.size > 0 && focusSet.has(e.from) && focusSet.has(e.to)
        const dim = focusSet.size > 0 && !inFocus
        return (
          <line
            key={i}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke={inFocus ? LAYER_COLOR[b.layer] : '#cbd5e1'}
            strokeWidth={inFocus ? 1.4 : 0.6}
            opacity={dim ? 0.1 : (inFocus ? 0.85 : 0.4)}
          />
        )
      })}

      {/* Nodes */}
      {positioned.map(n => {
        const isMatch = matches(n)
        const focus = focusSet.size > 0
        const inFocus = focus && focusSet.has(n.path)
        const isSelected = n.path === selected
        return (
          <g
            key={n.path}
            transform={`translate(${n.x}, ${n.y})`}
            onClick={() => onSelect(isSelected ? null : n.path)}
            style={{ cursor: 'pointer' }}
            opacity={!isMatch ? 0.18 : (focus && !inFocus ? 0.22 : 1)}
          >
            <circle
              r={isSelected ? 9 : (inFocus ? 7 : 5)}
              fill={LAYER_COLOR[n.layer] || '#94a3b8'}
              stroke={isSelected ? '#0f172a' : 'white'}
              strokeWidth={isSelected ? 2 : 1.5}
            />
            {(isSelected || inFocus) && (
              <text
                x={10}
                y={3}
                fontSize={10}
                fontFamily="ui-monospace, monospace"
                fill="#0f172a"
                fontWeight={isSelected ? 700 : 500}
              >
                {n.path.split('/').slice(-1)[0].replace('.lean', '')}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ============================================================================
// Page
// ============================================================================

function ResourceVerificationGraphPage() {
  const [deps, setDeps] = useState<RFDeps | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeLayers, setActiveLayers] = useState<Set<string>>(
    new Set(['L1', 'L2', 'L3', 'L4', 'SysLayer', 'Verifier', 'Corpus', 'Findings']),
  )
  const [selected, setSelected] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 1100, h: 700 })

  useEffect(() => {
    resourceFormalApi.getDeps()
      .then(setDeps)
      .catch(e => setError(e?.message || 'Failed to load dep graph'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      setSize({ w: Math.max(600, rect.width), h: Math.max(500, rect.height) })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const toggleLayer = (L: string) => {
    setActiveLayers(prev => {
      const next = new Set(prev)
      if (next.has(L)) next.delete(L); else next.add(L)
      return next
    })
  }

  const layerCounts = useMemo(() => {
    if (!deps) return {} as Record<string, number>
    const c: Record<string, number> = {}
    for (const n of deps.nodes) c[n.layer] = (c[n.layer] || 0) + 1
    return c
  }, [deps])

  const selectedNode = selected && deps ? deps.nodes.find(n => n.path === selected) || null : null

  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg flex flex-col">
      <header className="bg-white/85 backdrop-blur border-b border-qcloud-border px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80">
            <Logo size="small" />
            <span className="text-xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">QuantumArena</span>
          </Link>
          <span className="text-qcloud-border">·</span>
          <Link to="/resource-verification" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Resource Verification</Link>
          <span className="text-qcloud-border">›</span>
          <span className="text-sm font-semibold text-qcloud-text">Dependency graph</span>
        </div>
        <AuthHeader />
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Sidebar */}
        <aside className="w-72 bg-white border-r border-qcloud-border flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-4 border-b border-qcloud-border">
            <h2 className="font-bold text-qcloud-text">Lean import graph</h2>
            <p className="text-xs text-qcloud-muted mt-1">
              File-level <code>import</code> edges across the resourceFormal repo.
              Click a node to highlight its neighbours; toggle layers below to declutter.
            </p>
          </div>
          <div className="p-4 border-b border-qcloud-border">
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter by file or module…"
              className="w-full px-3 py-1.5 text-sm border border-qcloud-border rounded-lg focus:outline-none focus:ring-2 focus:ring-qcloud-primary/30"
            />
          </div>
          <div className="p-4 border-b border-qcloud-border">
            <div className="text-[11px] uppercase tracking-wider font-bold text-qcloud-muted mb-2">Layers</div>
            <div className="space-y-1">
              {LAYER_ORDER.map(L => {
                const count = layerCounts[L] || 0
                if (!count) return null
                const active = activeLayers.has(L)
                return (
                  <label
                    key={L}
                    className={`flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer ${active ? '' : 'opacity-40'} hover:bg-slate-50`}
                  >
                    <input type="checkbox" checked={active} onChange={() => toggleLayer(L)} className="accent-qcloud-primary" />
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: LAYER_COLOR[L] }} />
                    <span className="text-qcloud-text text-xs flex-1">{LAYER_LABEL[L]}</span>
                    <span className="text-[10px] text-qcloud-muted font-mono">{count}</span>
                  </label>
                )
              })}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {selectedNode ? (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-qcloud-muted mb-2">Selected</div>
                <div className="text-sm font-semibold text-qcloud-text mb-1">{selectedNode.module}</div>
                <div className="text-[11px] font-mono text-qcloud-muted mb-3 break-all">{selectedNode.path}</div>
                <Link
                  to={`/resource-verification?file=${encodeURIComponent(selectedNode.path)}`}
                  className="block text-center text-xs px-3 py-1.5 rounded bg-qcloud-primary/10 text-qcloud-primary hover:bg-qcloud-primary/20 font-medium mb-4"
                >
                  Open Lean source →
                </Link>
                <div className="text-[11px] uppercase tracking-wider font-bold text-qcloud-muted mb-1">Imports ({selectedNode.imports.length})</div>
                <div className="space-y-0.5 mb-3">
                  {selectedNode.imports.map(p => (
                    <button
                      key={p}
                      onClick={() => setSelected(p)}
                      className="block w-full text-left text-[11px] font-mono text-qcloud-text hover:text-qcloud-primary px-2 py-0.5 rounded hover:bg-slate-50 truncate"
                      title={p}
                    >
                      {p.split('/').slice(-2).join('/')}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-qcloud-muted mb-1">Imported by ({selectedNode.imported_by.length})</div>
                <div className="space-y-0.5">
                  {selectedNode.imported_by.map(p => (
                    <button
                      key={p}
                      onClick={() => setSelected(p)}
                      className="block w-full text-left text-[11px] font-mono text-qcloud-text hover:text-qcloud-primary px-2 py-0.5 rounded hover:bg-slate-50 truncate"
                      title={p}
                    >
                      {p.split('/').slice(-2).join('/')}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-xs text-qcloud-muted">
                Click any node in the graph to see its imports and reverse imports.
              </div>
            )}
          </div>
        </aside>

        {/* Graph */}
        <div ref={containerRef} className="flex-1 min-h-0 relative">
          {loading && <div className="absolute inset-0 flex items-center justify-center text-qcloud-muted">Computing layout…</div>}
          {error && <div className="absolute inset-0 flex items-center justify-center text-rose-600">{error}</div>}
          {deps && (
            <GraphSvg
              deps={deps}
              width={size.w}
              height={size.h}
              activeLayers={activeLayers}
              onSelect={setSelected}
              selected={selected}
              query={query}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ResourceVerificationGraphPage
